import React from 'react';
import { Button, Container, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import API, { useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { useErrorHandler } from '../errorHandler';
import { ValidatableField } from '../form';
const msgs = defineMessages({
    errorNoEmail: {
        id: 'pages.guest.EmailUnverifiedPage.error.noEmail',
        defaultMessage: 'email is null',
    },
    errorInvalidLogin: {
        id: 'pages.guest.ForgotPasswordPage.error.invalidLogin',
        defaultMessage: 'invalid login',
    },
    headerTitle: {
        id: 'pages.guest.ForgotPasswordPage.header.title',
        defaultMessage: 'Reset Password',
    },
    inputEmail: {
        id: 'pages.guest.ForgotPasswordPage.input.email',
        defaultMessage: 'Email',
    },
    buttonCancel: {
        id: 'pages.guest.ForgotPasswordPage.button.cancel',
        defaultMessage: 'Cancel',
    },
    buttonSubmit: {
        id: 'pages.guest.ForgotPasswordPage.button.submit',
        defaultMessage: 'Submit',
    },
    buttonBackToLoginPage: {
        id: 'pages.guest.ForgotPasswordPage.button.backToLoginPage',
        defaultMessage: 'Back to the login page',
    },
    textEmailSentOut: {
        id: 'pages.guest.ForgotPasswordPage.text.emailSentOut',
        defaultMessage: 'An email was sent out. Please follow the instruction to reset your password.',
    },
    textEmailToReset: {
        id: 'pages.guest.ForgotPasswordPage.text.emailToReset',
        defaultMessage: "Enter your email. We'll email you a link to reset your password.",
    },
});
function ForgotPasswordPage() {
    const intl = useIntl();
    const { csrfToken } = useCsrfToken();
    const navigate = useNavigate();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const { showSuccess } = useSnackbar();
    const { register, formState, handleSubmit: validate } = useForm({ mode: 'onChange' });
    const { errors } = formState;
    const [email, setEmail] = React.useState();
    const { mutate, isSuccess, isLoading } = useMutation(() => {
        if (!email) {
            return Promise.reject(intl.formatMessage(msgs.errorNoEmail));
        }
        return API.Accounts.resetPassword(csrfToken, email);
    }, {
        onSuccess: () => {
            showSuccess('Email sent');
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleSubmit = () => mutate();
    return (React.createElement(Container, { component: "main", sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, mt: 8, p: 3 } },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Typography, { variant: "h6", align: "center" }, intl.formatMessage(msgs.headerTitle)),
                    isSuccess ? (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { variant: "body1" }, intl.formatMessage(msgs.textEmailSentOut)),
                        React.createElement(Button, { onClick: () => navigate('/'), startIcon: React.createElement(ArrowBackIcon, null), size: "medium" }, intl.formatMessage(msgs.buttonBackToLoginPage)))) : (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { variant: "body1" }, intl.formatMessage(msgs.textEmailToReset)),
                        React.createElement(ValidatableField, { id: "email", type: "email", label: intl.formatMessage(msgs.inputEmail), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                                required: true,
                                pattern: /\S+@\S+\.\S+/,
                                maxLength: 128,
                                onChange: (e) => setEmail(e.target.value),
                            } }),
                        React.createElement(Stack, { direction: "row", gap: 1 },
                            React.createElement(Button, { disabled: isLoading, fullWidth: true, onClick: () => navigate('/'), size: "medium" }, intl.formatMessage(msgs.buttonCancel)),
                            React.createElement(Button, { disabled: isLoading || !email, color: "primary", fullWidth: true, type: "submit", size: "medium" }, intl.formatMessage(msgs.buttonSubmit))))))))));
}
export default React.memo(ForgotPasswordPage);
